import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { routes } from '@/lib/routes';
import type { TimelineSession, TimelineMilestone } from '@/types/coach';
import { Button } from '@/components/ui/button';
import { TimelineGoalSticky } from './TimelineGoalSticky';
import { TimelineAnchor } from './TimelineAnchor';
import { TimelineSessionCard } from './TimelineSessionCard';
import { TimelineGap } from './TimelineGap';
import { TimelineMilestoneCard } from './TimelineMilestoneCard';
import { TimelineAddMilestoneButton } from './TimelineAddMilestoneButton';
import { MilestoneDialog, type MilestoneEditInput } from './MilestoneDialog';

interface Props {
  clientId: string;
  goal: string | null;
  createdAt: string; // ISO
  onCreateSession: () => void; // wechselt in Sessions-Tab und löst Anlege-Flow aus
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function calcGapPx(gapDays: number): number {
  return Math.min(Math.max(gapDays * 4, 16), 240);
}

function diffDays(a: string, b: string): number {
  return Math.max(
    0,
    Math.round((new Date(b).getTime() - new Date(a).getTime()) / MS_PER_DAY),
  );
}

type StreamItem =
  | { type: 'session'; data: TimelineSession; sessionIndex: number }
  | { type: 'milestone'; data: TimelineMilestone };

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create'; defaultDate?: string }
  | { mode: 'edit'; milestone: MilestoneEditInput };

export function ClientTimeline({ clientId, goal, createdAt, onCreateSession }: Props) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<TimelineSession[]>([]);
  const [milestones, setMilestones] = useState<TimelineMilestone[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [dialogState, setDialogState] = useState<DialogState>({ mode: 'closed' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.coach
      .timeline(clientId)
      .then((res) => {
        if (cancelled) return;
        setSessions(res.sessions);
        setMilestones(res.milestones);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, reloadCounter]);

  function reload() {
    setReloadCounter((c) => c + 1);
  }

  async function handleDeleteMilestone(id: string) {
    if (!confirm('Meilenstein wirklich löschen?')) return;
    try {
      await api.coach.milestones.remove(id);
      reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
        <p className="text-destructive">{error}</p>
        <Button size="sm" variant="outline" className="mt-3" onClick={reload}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  const today = new Date().toISOString();
  const todayMs = new Date(today).getTime();

  // Build merged chronological stream
  const sessionItems: StreamItem[] = sessions.map((s, i) => ({
    type: 'session',
    data: s,
    sessionIndex: i,
  }));
  const milestoneItems: StreamItem[] = milestones.map((m) => ({
    type: 'milestone',
    data: m,
  }));
  const merged: StreamItem[] = [...sessionItems, ...milestoneItems].sort(
    (a, b) => new Date(a.data.date).getTime() - new Date(b.data.date).getTime(),
  );

  const pastCount = (() => {
    const idx = merged.findIndex((it) => new Date(it.data.date).getTime() > todayMs);
    return idx === -1 ? merged.length : idx;
  })();

  function renderStreamItem(item: StreamItem, prevDate: string, marginTop: number) {
    const gapDays = diffDays(prevDate, item.data.date);
    if (item.type === 'session') {
      const s = item.data;
      return (
        <div key={`s-${s.id}`} className="group" style={{ marginTop }}>
          <div className="relative">
            <TimelineGap gapDays={gapDays} />
            <TimelineSessionCard
              session={s}
              index={item.sessionIndex + 1}
              onClick={() => navigate(routes.coach.session(clientId, s.id))}
            />
          </div>
          <TimelineAddMilestoneButton
            variant="inline"
            onClick={() =>
              setDialogState({ mode: 'create', defaultDate: s.date })
            }
          />
        </div>
      );
    }
    const m = item.data;
    const linkedSession = m.sessionId
      ? sessions.find((s) => s.id === m.sessionId)
      : undefined;
    return (
      <div key={`m-${m.id}`} className="group" style={{ marginTop }}>
        <div className="relative">
          <TimelineGap gapDays={gapDays} />
          <TimelineMilestoneCard
            milestone={m}
            linkedSessionDate={linkedSession?.date}
            onEdit={() =>
              setDialogState({
                mode: 'edit',
                milestone: {
                  id: m.id,
                  date: m.date,
                  title: m.title,
                  description: m.description,
                  sessionId: m.sessionId,
                },
              })
            }
            onDelete={() => handleDeleteMilestone(m.id)}
          />
        </div>
      </div>
    );
  }

  function getPrevDate(idx: number, isFirstFuture: boolean): string {
    if (idx === 0) return createdAt;
    if (isFirstFuture) return today;
    return merged[idx - 1].data.date;
  }

  return (
    <div>
      {goal && goal.trim() && <TimelineGoalSticky goal={goal} />}

      <div className="relative pl-2">
        <div className="absolute bottom-1 left-[7.5px] top-1 w-[2px] bg-border" />

        <div className="relative">
          <TimelineAnchor type="start" date={createdAt} />
        </div>

        {merged.length === 0 ? (
          <>
            <div
              className="my-6 rounded-md border border-dashed border-border bg-muted/40 p-6 text-center"
              style={{ marginLeft: '24px' }}
            >
              <p className="text-sm text-muted-foreground">
                Noch keine Sessions oder Meilensteine erfasst.
              </p>
              <Button size="sm" className="mt-3" onClick={onCreateSession}>
                Erste Session anlegen
              </Button>
            </div>
            <div style={{ marginTop: 24 }}>
              <TimelineAnchor type="today" date={today} />
            </div>
          </>
        ) : (
          <>
            {merged.map((item, idx) => {
              const isFirstFuture = idx === pastCount && idx > 0;
              const prevDate = getPrevDate(idx, isFirstFuture);
              const marginTop = idx === 0 ? 24 : calcGapPx(diffDays(prevDate, item.data.date));

              return (
                <div key={`wrap-${idx}`}>
                  {isFirstFuture && (
                    <div
                      style={{
                        marginTop: calcGapPx(
                          diffDays(merged[pastCount - 1].data.date, today),
                        ),
                      }}
                    >
                      <TimelineAnchor type="today" date={today} />
                    </div>
                  )}
                  {renderStreamItem(item, prevDate, marginTop)}
                </div>
              );
            })}
            {pastCount === merged.length && (
              <div
                style={{
                  marginTop: calcGapPx(
                    diffDays(merged[merged.length - 1].data.date, today),
                  ),
                }}
              >
                <TimelineAnchor type="today" date={today} />
              </div>
            )}
          </>
        )}

        <div className="mt-6 pl-6">
          <TimelineAddMilestoneButton
            variant="cta"
            onClick={() => setDialogState({ mode: 'create' })}
          />
        </div>
      </div>

      {dialogState.mode !== 'closed' && (
        <MilestoneDialog
          clientId={clientId}
          sessions={sessions}
          initial={dialogState.mode === 'edit' ? dialogState.milestone : undefined}
          defaultDate={dialogState.mode === 'create' ? dialogState.defaultDate : undefined}
          onClose={() => setDialogState({ mode: 'closed' })}
          onSaved={() => {
            setDialogState({ mode: 'closed' });
            reload();
          }}
        />
      )}
    </div>
  );
}
